/** Back office → Campaigns → one campaign: Meta ids, pause/resume (confirmed), daily stats, ROI, history. */

import {
  useAdsCampaignsPauseCreate,
  useAdsCampaignsReportRetrieve,
  useAdsCampaignsResumeCreate,
  useAdsCampaignsRetrieve,
  useAdsCampaignsRetryCreate,
  useAdsCampaignsSyncCreate,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, GlassSheet, Skeleton } from "@agencyos/ui-web";
import { ArrowLeft, Pause, Play, RefreshCw, Send } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Page } from "../../components/AppShell";
import { dateText, localeOf, TIME_ZONE } from "../billing/shared";
import "./ads.css";
import { SpendChart } from "./SpendChart";
import { MetricsGrid, ModeNotice, STATUS_TONE, useAdsLive, useRefreshAds } from "./shared";

function when(value: string | null | undefined, language: string): string {
  if (!value) return "";
  return new Date(value).toLocaleString(localeOf(language), { timeZone: TIME_ZONE, dateStyle: "short", timeStyle: "short" });
}

export function AdCampaignPage() {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const refresh = useRefreshAds();
  const live = useAdsLive();
  const detail = useAdsCampaignsRetrieve(id, {
    // Meta is called by a background task: ask again while an action is in progress.
    query: { refetchInterval: (q) => (q.state.data?.status === 200 && q.state.data.data.pending_action ? 2000 : false) },
  });
  const report = useAdsCampaignsReportRetrieve(id);
  const pause = useAdsCampaignsPauseCreate({ mutation: { onSuccess: refresh } });
  const resume = useAdsCampaignsResumeCreate({ mutation: { onSuccess: refresh } });
  const retry = useAdsCampaignsRetryCreate({ mutation: { onSuccess: refresh } });
  const sync = useAdsCampaignsSyncCreate({ mutation: { onSuccess: refresh } });
  const [confirming, setConfirming] = useState(false);
  const campaign = detail.data?.status === 200 ? detail.data.data : undefined;
  const metrics = report.data?.status === 200 ? report.data.data : undefined;

  if (!campaign) {
    return (
      <Page title={t("ads.title")}>
        {detail.isError ? (
          <p role="alert" className="ko">
            {t("common.error")}
          </p>
        ) : (
          <Skeleton style={{ height: 200 }} />
        )}
      </Page>
    );
  }

  const busy = Boolean(campaign.pending_action) || pause.isPending || resume.isPending;
  const kind = t(`ads.budgetKind.${campaign.budget_kind ?? "daily"}`);
  const ids: [string, string][] = [
    [t("ads.detail.campaignId"), campaign.meta_campaign_id],
    [t("ads.detail.adsetId"), campaign.meta_adset_id],
    [t("ads.detail.creativeId"), campaign.meta_creative_id],
    [t("ads.detail.adId"), campaign.meta_ad_id],
    [t("ads.detail.trackingCode"), campaign.tracking_code],
  ];

  const onResume = () =>
    resume.mutate({ id, data: { confirm: true } }, { onSettled: () => setConfirming(false) });

  return (
    <Page
      title={campaign.name}
      description={`${campaign.budget_text} · ${kind}`}
      actions={
        <Link className="link" to="/ads">
          <ArrowLeft size={16} aria-hidden="true" /> {t("ads.detail.back")}
        </Link>
      }
    >
      <ModeNotice live={campaign.is_test ? false : live} />
      <GlassCard className="stack">
        <div className="row">
          <Badge tone={STATUS_TONE[campaign.status]}>{t(`ads.status.${campaign.status}`)}</Badge>
          <Badge tone={campaign.is_test ? "default" : "danger"}>{campaign.is_test ? t("ads.mode.test") : t("ads.mode.live")}</Badge>
          {campaign.offer_code ? <Badge tone="offer">{campaign.offer_code}</Badge> : null}
        </div>
        {campaign.pending_action ? (
          <p role="status" className="muted">
            {t(`ads.pending.${campaign.pending_action}`)}
          </p>
        ) : null}
        {campaign.last_error ? (
          <p role="alert" className="ko">
            {t("ads.detail.error", { error: campaign.last_error })}
          </p>
        ) : null}
        <div className="row">
          {campaign.status === "paused" ? (
            <Button icon={<Play size={16} aria-hidden="true" />} disabled={busy} onClick={() => setConfirming(true)}>
              {t("ads.detail.resume")}
            </Button>
          ) : null}
          {campaign.status === "active" ? (
            <Button variant="secondary" icon={<Pause size={16} aria-hidden="true" />} disabled={busy} onClick={() => pause.mutate({ id })}>
              {t("ads.detail.pause")}
            </Button>
          ) : null}
          {campaign.status === "draft" && !campaign.pending_action ? (
            <Button variant="secondary" icon={<Send size={16} aria-hidden="true" />} disabled={retry.isPending} onClick={() => retry.mutate({ id })}>
              {t("ads.detail.retry")}
            </Button>
          ) : null}
          {campaign.meta_campaign_id ? (
            <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} disabled={sync.isPending} onClick={() => sync.mutate({ id })}>
              {t("ads.detail.sync")}
            </Button>
          ) : null}
        </div>
        <p className="muted">
          {t("ads.detail.lastSync", { when: when(campaign.last_synced_at, i18n.language) || t("ads.detail.never") })}
        </p>
      </GlassCard>

      <GlassCard className="stack" aria-labelledby="ads-report-title">
        <h2 id="ads-report-title">{t("ads.detail.report")}</h2>
        {metrics ? <MetricsGrid metrics={metrics} label={t("ads.detail.report")} /> : <Skeleton style={{ height: 90 }} />}
      </GlassCard>

      <GlassCard className="stack" aria-labelledby="ads-stats-title">
        <h2 id="ads-stats-title">{t("ads.detail.stats")}</h2>
        {campaign.stats.length ? (
          <>
            <SpendChart stats={campaign.stats} />
            <div className="table-wrap">
              <table className="table" aria-label={t("ads.detail.stats")}>
                <thead>
                  <tr>
                    <th>{t("ads.detail.day")}</th>
                    <th className="num">{t("ads.detail.spend")}</th>
                    <th className="num">{t("ads.detail.impressions")}</th>
                    <th className="num">{t("ads.detail.clicks")}</th>
                    <th className="num">{t("ads.detail.conversations")}</th>
                  </tr>
                </thead>
                <tbody>
                  {campaign.stats.map((s) => (
                    <tr key={s.day}>
                      <td className="nowrap">{dateText(s.day, i18n.language)}</td>
                      <td className="num nowrap">{formatXaf(s.spend_xaf)}</td>
                      <td className="num">{s.impressions}</td>
                      <td className="num">{s.clicks}</td>
                      <td className="num">{s.conversations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="muted">{t("ads.detail.noStats")}</p>
        )}
      </GlassCard>

      <GlassCard className="stack" aria-labelledby="ads-ids-title">
        <h2 id="ads-ids-title">{t("ads.detail.ids")}</h2>
        <dl className="ads-ids">
          {ids.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value || "—"}</dd>
            </div>
          ))}
          <div>
            <dt>{t("ads.detail.message")}</dt>
            <dd>{campaign.message}</dd>
          </div>
          {campaign.link ? (
            <div>
              <dt>{t("ads.detail.link")}</dt>
              <dd>
                <a className="link" href={campaign.link} target="_blank" rel="noreferrer">
                  {campaign.link}
                </a>
              </dd>
            </div>
          ) : null}
        </dl>
      </GlassCard>

      <GlassCard className="stack" aria-labelledby="ads-history-title">
        <h2 id="ads-history-title">{t("ads.detail.history")}</h2>
        {campaign.history.length ? (
          <ol className="ads-history">
            {campaign.history.map((h) => (
              <li key={h.id} className="stack">
                <div className="row">
                  <strong>{t(`ads.detail.action.${h.action}`)}</strong>
                  <Badge tone={h.result === "failed" ? "danger" : h.result === "ok" ? "success" : "default"}>
                    {t(`ads.detail.result.${h.result ?? "pending"}`)}
                  </Badge>
                  <span className="muted">{when(h.created_at, i18n.language)}</span>
                  {h.by_name ? <span className="muted">· {h.by_name}</span> : null}
                </div>
                {h.error ? <span className="ko">{h.error}</span> : null}
              </li>
            ))}
          </ol>
        ) : (
          <p className="muted">{t("ads.detail.noHistory")}</p>
        )}
      </GlassCard>

      <GlassSheet open={confirming} title={t("ads.confirm.title")} onClose={() => setConfirming(false)}>
        <div className="stack">
          <p className={campaign.is_test ? "muted" : "notice-warning"}>
            {campaign.is_test ? t("ads.confirm.test") : t("ads.confirm.live", { budget: campaign.budget_text, kind })}
          </p>
          {resume.isError ? (
            <p role="alert" className="ko">
              {t("common.error")}
            </p>
          ) : null}
          <div className="row">
            <Button disabled={resume.isPending} onClick={onResume}>
              {resume.isPending ? t("common.loading") : t("ads.confirm.accept")}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      </GlassSheet>
    </Page>
  );
}
